import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Skeleton, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Icons, type Nil, Text, Utils } from '@pbt/pbt-ui-components'
import { MembershipStatus } from '@pbt/pbt-ui-components/src/constants/wellnessPlans'
import {
  Wellness as WellnessIcon,
  Wings as WingsIcon,
} from '@pbt/pbt-ui-components/src/icons'

import {
  type PreviousMedicalRecord,
  useFetchIsNewPatientQuery,
  useFetchPatientWithPmrDataOmnichannelQuery,
  useFetchPatientWithPmrDataQuery,
} from '~/api/graphql/generated/types'
import {
  AgentIngressButton,
  AgentIngressPromptType,
} from '~/components/agent/ingress'
import AlertIcon from '~/components/common/labels/AlertIcon'
import AlertLabel from '~/components/common/labels/AlertLabel'
import PatientInfoLabel from '~/components/common/labels/PatientInfoLabel'
import Link from '~/components/common/link/Link'
import PuiTabs from '~/components/common/PuiTabs'
import useGetPreferredContactMethod from '~/components/dashboard/clients/details/new-client-and-patient/useGetPreferredContactMethod'
import { PatientPreferencesChip } from '~/components/dashboard/clients/patient/PatientPreferencesChip'
import { ClientPreferencesChip } from '~/components/dashboard/clients/preferences/ClientPreferencesChip'
import PatientWarningAvatar from '~/components/dashboard/timetable/PatientWarningAvatar'
import WellnessPlanMemberButton from '~/components/dashboard/wellness-plans/WellnessPlanMemberButton'
import { useClientMembershipStatus } from '~/components/dashboard/wellness-plans/wellnessPlanUtils'
import { AlertColorLevel } from '~/constants/alertColors'
import { AppointmentState } from '~/constants/appointmentStates'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { useAgentIngress } from '~/hooks/useAgentIngress'
import { updatePatientIsNewPatient } from '~/store/actions/patients'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type { TimetableEvent } from '~/types'
import { getPatientStatus } from '~/utils'
import useDialog from '~/utils/useDialog'

import AppointmentLastVisitInfoLabel from './AppointmentLastVisitInfoLabel'
import FormsList from './forms/FormsList'
import PreviousMedicalRecords from './PreviousMedicalRecords'
import AllReminders from './reminders/AllReminders'
import UpcomingAndOverdueReminders from './reminders/UpcomingAndOverdueReminders'

import styles from './AppointmentClientAndPatientDetails.module.scss'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      maxHeight: 'max(380px, calc(100vh - 14rem))',
      minHeight: '100%',
      backgroundColor: theme.colors.dialogHeaderBackground,
      overflowY: 'auto',
      '& > *': {
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
    contactMethodText: {
      wordBreak: 'break-word',
      paddingLeft: theme.spacing(0.5),
    },
    link: {
      fontWeight: 500,
    },
    avatar: {
      width: 72,
      height: 72,
    },
    tabRoot: {
      padding: theme.spacing(1.5),
    },
    tabRootCollapsed: {
      padding: `0 ${theme.spacing(1.5)}`,
    },
    tabs: {
      minHeight: 0,
      marginTop: theme.spacing(0.5),
      borderBottom: theme.constants.tableBorder,
    },
    remindersTitle: {
      paddingTop: theme.spacing(1),
    },
    wpButton: {
      padding: theme.spacing(0, 1),
      width: 'fit-content',
    },
    chip: {
      width: 16,
      height: 16,
      marginRight: theme.spacing(0.5),
      overflow: 'visible',
    },
    largeChip: {
      width: 20,
      height: 20,
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      overflow: 'visible',
    },
    title: {
      fontSize: '1.4rem',
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1.5),
    },
  }),
  { name: 'AppointmentClientAndPatientDetails' },
)

export interface AppointmentClientAndPatientDetailsProps {
  appointment?: TimetableEvent | Nil
  clientId: string
  onChangeRequested?: () => void
  patientId: string
}

const AppointmentClientAndPatientDetails = ({
  clientId,
  patientId,
  onChangeRequested,
  appointment,
}: AppointmentClientAndPatientDetailsProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))
  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)
  const isSOAPConsentFormsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SOAP_CONSENT_FORMS),
  )
  const isPmrConsentEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PMR_NEW_APPOINTMENT),
  )
  const { handleIngress } = useAgentIngress({
    patientId,
    clientId,
  })

  const { t } = useTranslation(['Common', 'TimeTable', 'Reminders'])
  const isTouch = useMediaQuery('(pointer:coarse)')

  const tabs = [
    {
      label: t('TimeTable:UPCOMING_OR_OVERDUE_REMINDERS'),
      component: UpcomingAndOverdueReminders,
    },
    {
      label: t('TimeTable:ALL_REMINDERS'),
      component: AllReminders,
    },
  ]

  const [selectedTab, setSelectedTab] = useState(0)

  const Component = tabs[selectedTab].component
  const patientStatus = getPatientStatus(patient)
  const preferredContactMethod = useGetPreferredContactMethod({ client })
  const membershipStatus = useClientMembershipStatus(clientId)
  const appointmentIsCancelledOrNoShow =
    appointment?.state?.name === AppointmentState.CANCELLED ||
    appointment?.state?.name === AppointmentState.NO_SHOW
  const showPreviousMedicalRecordsSection = Boolean(
    isPmrConsentEnabled &&
    appointment?.id &&
    !appointmentIsCancelledOrNoShow &&
    patient?.isNewPatient,
  )

  // Fetch isNewPatient status from API
  const { data: isNewPatientData, loading: isLoadingNewPatientData } =
    useFetchIsNewPatientQuery({
      variables: { patientId },
      skip: !isPmrConsentEnabled,
    })

  // Update Redux when isNewPatient data is fetched
  useEffect(() => {
    const isNewPatient = isNewPatientData?.patient?.isNewPatient
    if (isNewPatient !== undefined && isNewPatient !== null) {
      dispatch(updatePatientIsNewPatient(patientId, isNewPatient))
    }
  }, [isNewPatientData, patientId, dispatch])

  const {
    data: pmrData,
    loading: pmrLoading,
    error: pmrError,
  } = (
    isOmnichannel
      ? useFetchPatientWithPmrDataOmnichannelQuery
      : useFetchPatientWithPmrDataQuery
  )({
    variables: { patientId },
    skip: !showPreviousMedicalRecordsSection,
  })
  const previousMedicalRecord: PreviousMedicalRecord | Nil =
    pmrData?.patient?.previousMedicalRecord

  const [openEditPMRDialog] = useDialog(DialogNames.EDIT_PMR)

  const handleEditPreviousMedicalRecords = () => {
    if (!appointment || !patient) return
    openEditPMRDialog({
      appointment,
      patient,
      clientId,
      previousMedicalRecord,
    })
  }

  return (
    <Grid
      container
      item
      className={classes.root}
      flexDirection="column"
      wrap="nowrap"
    >
      <Grid container item direction="column" pt={5} px={2}>
        <Grid container item xs alignItems="center">
          {client?.alertText && (
            <AlertIcon
              alertColorId={client.alertColorId}
              alertColorLevel={AlertColorLevel.CLIENT}
              alertText={client.alertText}
              size={isTouch ? 'medium' : 'small'}
            />
          )}
          <ClientPreferencesChip
            appointment={appointment}
            className={isTouch ? classes.largeChip : classes.chip}
            clientId={clientId}
          />
          {isOmnichannel &&
            (membershipStatus === MembershipStatus.PAUSED ||
              membershipStatus === MembershipStatus.ACTIVE) && (
              <WellnessIcon
                className={isTouch ? classes.largeChip : classes.chip}
                variant={
                  membershipStatus === MembershipStatus.PAUSED
                    ? Icons.IconVariant.PAUSED
                    : Icons.IconVariant.PRIMARY
                }
              />
            )}
          <Link
            className={classes.link}
            rel="noopener noreferrer"
            target="_blank"
            to={`/client/${clientId}`}
          >
            {Utils.getPersonString(client)}
          </Link>
          <Text className={classes.contactMethodText} variant="body">
            {preferredContactMethod.value}
          </Text>
        </Grid>
        <Grid container item alignItems="center" my={2} wrap="nowrap">
          <Grid item mr={1}>
            <PatientWarningAvatar
              classes={{
                avatar: classes.avatar,
              }}
              patientId={patientId}
            />
          </Grid>
          <Grid container item direction="column">
            <Grid container item alignItems="center" wrap="nowrap">
              <PatientPreferencesChip
                appointment={appointment}
                className={isTouch ? classes.largeChip : classes.chip}
                patientId={patientId}
              />
              <Link
                className={classes.link}
                rel="noopener noreferrer"
                target="_blank"
                to={`/patient/${patientId}`}
              >
                {patient?.name}
              </Link>
              {patient?.deceased && <WingsIcon />}
              {patient?.active === false && (
                <Grid item ml={1}>
                  <AlertLabel
                    small
                    message={t('Common:INACTIVE_ONE')}
                    variant="normal"
                  />
                </Grid>
              )}
              {isPmrConsentEnabled && (
                <>
                  {isLoadingNewPatientData ? (
                    <Skeleton
                      height={24}
                      sx={{ mx: 1 }}
                      variant="text"
                      width={80}
                    />
                  ) : (
                    <>
                      {patient?.isNewPatient && (
                        <Grid item className={styles.newPatientTag}>
                          <span>{t('Common:NEW_PATIENT')}</span>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
              {onChangeRequested && (
                <Text
                  link
                  disabled={patientStatus.disabled}
                  ml={1}
                  variant="body2"
                  onClick={onChangeRequested}
                >
                  {t('Common:CHANGE_ACTION')}
                </Text>
              )}
            </Grid>
            <PatientInfoLabel patientId={patientId} />
            <Grid container item gap={0.5} marginTop={0.5}>
              <WellnessPlanMemberButton
                className={classes.wpButton}
                clientId={client?.id}
                patientId={patient?.id}
              />
              <AgentIngressButton
                text={t('Agent:SUMMARIZE_HISTORY')}
                onClick={() =>
                  handleIngress(AgentIngressPromptType.MEDICAL_HISTORY)
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item px={2}>
        <AppointmentLastVisitInfoLabel patientId={patientId} />
      </Grid>
      {isSOAPConsentFormsEnabled && appointment && (
        <FormsList appointment={appointment} />
      )}
      {isSOAPConsentFormsEnabled && (
        <Grid item className={classes.remindersTitle}>
          <Text className={classes.title} variant="h2">
            {t('Reminders:REMINDERS_LIST.REMINDERS')}
          </Text>
        </Grid>
      )}
      <Grid container item direction="column">
        <Grid item>
          <PuiTabs
            PuiTabsProps={
              isSOAPConsentFormsEnabled
                ? {
                    className: classes.tabs,
                  }
                : undefined
            }
            classes={{
              tabRoot: isSOAPConsentFormsEnabled
                ? classes.tabRootCollapsed
                : classes.tabRoot,
            }}
            selectedTab={selectedTab}
            tabs={tabs}
            onSelectedTabChange={setSelectedTab}
          />
        </Grid>
        <Grid item>
          <Component patientId={patientId} />
        </Grid>
      </Grid>
      {showPreviousMedicalRecordsSection && (
        <PreviousMedicalRecords
          error={pmrError}
          loading={pmrLoading}
          previousMedicalRecord={previousMedicalRecord}
          onEdit={handleEditPreviousMedicalRecords}
        />
      )}
    </Grid>
  )
}

export default AppointmentClientAndPatientDetails
